<script setup>
/**
 * 「推广收益」页，逐条对齐旧版 web/12.0195d2b4b240a93b0284.js 里的
 * app-money 组件（类 m、模板工厂 B + 子模板 T/A、样式 N）。
 * 旧版路由是 /tabs/recommend/money。
 *
 * 旧版类 m：
 *   ngOnInit → getRecommendMoney()
 *   getRecommendMoney(): POST /game/recommend/money {}
 *     成功 → lists = data.lists、total = data.total
 *     注意旧版传的是空对象，后端 GameRecommend::money() 里
 *     start 默认 'yesterday'、end 默认 'today'，所以看到的是昨天到今天的数据
 *   settle(level = 0):
 *     alert{header:'确认', message:'确定领取吗？', buttons:[取消, 确认]}
 *     确认后 POST /game/recommend/settle {level}
 *     成功 → toast「领取成功」+ 重新 getRecommendMoney()
 *
 * 表格（旧版是一个 table.table，不是 ion-list）：
 *   表头：级别 / 投注量 / 收益率 / 总收益 / 未结算 / 操作
 *   每行：{{recommend_level}}级 / bet_money_calculate / bet_odds_recommend % /
 *         bet_money_recommend / bet_money_recommend_n /
 *         红色小按钮「领取」，[disabled]="bet_money_recommend_n <= 0"
 *   合计行：「合计：」/ total.bet_money_calculate / 「-」/ total.bet_money_recommend /
 *           total.bet_money_recommend_n / 红色小按钮「领取 」（调 settle() 不带 level）
 *
 * 样式 N：
 *   .lists-item{display:flex;align-items:center;width:100%}
 *   .lists-item > div{flex-grow:1}
 *   .table{width:100%;text-align:center;border:1px solid #ccc;font-size:.8rem}
 *   .table tr{border:1px solid #ccc;min-height:2rem;line-height:2rem}
 *   .table tr td{border:1px solid #ccc;width:16.66%}
 */
import { onMounted, ref } from 'vue'
import { request } from '../services/api'
import LegacyAlert from '../components/LegacyAlert.vue'
import AppBottomNav from '../components/AppBottomNav.vue'

const lists = ref(null)
const total = ref(null)
const notice = ref('')
let noticeTimer = null
// 旧版 settle() 前的确认 alert；level 为 0 表示合计行的「领取」
const confirmLevel = ref(null)

function showToast(message) {
  notice.value = message
  if (noticeTimer) window.clearTimeout(noticeTimer)
  noticeTimer = window.setTimeout(() => { notice.value = '' }, 2000)
}

// 旧版 DecimalPipe '.2-2'
const decimal2 = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
function fmt2(value) { return decimal2.format(Number(value || 0)) }

/** 旧版 getRecommendMoney() */
async function getRecommendMoney() {
  try {
    // 旧版传的是空对象，start/end 走后端默认值（yesterday / today）
    const response = await request('/game/recommend/money', { method: 'POST', data: {} })
    lists.value = response.data?.lists || null
    total.value = response.data?.total || null
  } catch (error) {
    showToast(error.message || '加载失败')
  }
}

/** 旧版 settle(level = 0) 的确认 alert */
function askSettle(level = 0) { confirmLevel.value = level }

async function doSettle() {
  const level = confirmLevel.value
  confirmLevel.value = null
  if (level === null) return
  try {
    await request('/game/recommend/settle', { method: 'POST', data: { level } })
    showToast('领取成功')
    await getRecommendMoney()
  } catch (error) {
    showToast(error.message || '领取失败')
  }
}

onMounted(() => { void getRecommendMoney() })
</script>

<template>
  <div class="recommend-money-page">
    <!-- 旧版 ion-header > ion-toolbar，返回按钮 routerLink="/tabs/recommend" -->
    <header class="modal-header">
      <RouterLink class="modal-back" to="/user/recommend" aria-label="返回">
        <svg class="modal-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" fill="currentColor" /></svg>
      </RouterLink>
      <h1 class="modal-title">推广收益</h1>
      <span class="modal-spacer" aria-hidden="true"></span>
    </header>

    <!-- 旧版 ion-content[padding] -->
    <div class="modal-content">
      <table class="table">
        <tbody>
          <tr>
            <th>级别</th>
            <th>投注量</th>
            <th>收益率</th>
            <th>总收益</th>
            <th>未结算</th>
            <th>操作</th>
          </tr>

          <!-- 旧版 A → T：*ngIf="lists" -->
          <template v-if="lists">
            <tr v-for="(row, index) in lists" :key="row.recommend_level ?? index">
              <td>{{ row.recommend_level }}级</td>
              <td>{{ fmt2(row.bet_money_calculate) }}</td>
              <td>{{ row.bet_odds_recommend }} %</td>
              <td>{{ fmt2(row.bet_money_recommend) }}</td>
              <td>{{ fmt2(row.bet_money_recommend_n) }}</td>
              <td>
                <button
                  class="ion-button button-small button-solid ion-color-danger"
                  type="button"
                  :class="{ 'button-disabled': Number(row.bet_money_recommend_n) <= 0 }"
                  :disabled="Number(row.bet_money_recommend_n) <= 0"
                  @click="askSettle(row.recommend_level)"
                >
                  <span class="button-inner">领取</span>
                </button>
              </td>
            </tr>
          </template>

          <!-- 旧版的合计行。注意「收益率」这一格旧版是写死的「-」 -->
          <tr>
            <td>合计：</td>
            <td>{{ fmt2(total?.bet_money_calculate) }}</td>
            <td>-</td>
            <td>{{ fmt2(total?.bet_money_recommend) }}</td>
            <td>{{ fmt2(total?.bet_money_recommend_n) }}</td>
            <td>
              <!-- 旧版这个按钮调的是 settle() 不带 level，后端就会结算全部级别 -->
              <button
                class="ion-button button-small button-solid ion-color-danger"
                type="button"
                :class="{ 'button-disabled': Number(total?.bet_money_recommend_n) <= 0 }"
                :disabled="Number(total?.bet_money_recommend_n) <= 0"
                @click="askSettle(0)"
              >
                <span class="button-inner">领取 </span>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- 旧版 settle 的确认 alert -->
    <Teleport to="body">
      <LegacyAlert
        v-if="confirmLevel !== null"
        header="确认"
        message="确定领取吗？"
        cancel-text="取消"
        ok-text="确认"
        @cancel="confirmLevel = null"
        @confirm="doSettle"
      />
    </Teleport>

    <!-- 旧版 appService.showToast()：底部浮层 -->
    <div v-if="notice" class="ion-toast">
      <div class="toast-wrapper"><div class="toast-message">{{ notice }}</div></div>
    </div>

    <!-- 旧版这一页的路由是 /tabs/recommend/money，属于 tabs 的子路由，
         ion-tabs 的 tab 栏在子页面里一直挂着，所以这里也带底部导航 -->
    <AppBottomNav />
  </div>
</template>

<style scoped>
/* 页面外壳与顶栏跟其它旧版还原页保持一致 */
/* padding-bottom 是给底部 tab 栏（fixed，56px）留位 */
.recommend-money-page{position:fixed;z-index:1;top:0;left:0;width:100%;height:100%;background:#fff;color:#000;display:flex;flex-direction:column;font:14px Arial,"Microsoft YaHei",sans-serif;padding-bottom:56px;box-sizing:border-box}
.modal-header{display:flex;align-items:center;height:56px;background:var(--legacy-theme,#f95454);color:var(--legacy-toolbar-color,#fff);position:relative;z-index:1;box-shadow:0 2px 4px rgb(0 0 0 / 20%)}
.modal-back{display:flex;align-items:center;justify-content:center;width:56px;height:56px;border:0;background:transparent;line-height:1;color:inherit;cursor:pointer;padding:0;text-decoration:none}
.modal-back .modal-icon{width:24px;height:24px;display:block}
.modal-title{flex:1;margin:0;font-size:18px;font-weight:500;text-align:center;color:inherit}
.modal-spacer{width:56px;flex:none}
/* ion-content[padding] → 四边 16px */
.modal-content{flex:1;overflow-y:auto;-webkit-overflow-scrolling:touch;padding:16px}

/* --- 以下逐条照抄旧版样式表 N（chunk 12 的 app-money） --- */
.lists-item{display:flex;align-items:center;width:100%}
.lists-item > div{flex-grow:1}
.table{width:100%;text-align:center;border:1px solid #ccc;font-size:.8rem}
.table tr{border:1px solid #ccc;min-height:2rem;line-height:2rem}
.table tr td{border:1px solid #ccc;width:16.66%}
/* 旧版样式表 N 里只有上面这三条：没有 th 规则、也没有 border-collapse。
   所以旧版表头行（th）本身是没有竖线边框的、走浏览器默认的粗体居中，
   整张表也是默认的 border-collapse:separate（边框之间有 2px 间隙）。
   这里刻意不补规则，保持和旧版一致。 */

/* ion-button color="danger" size="small"，取自 web/75（md） */
.ion-button{
  display:inline-flex;align-items:center;justify-content:center;
  margin:4px 2px;border:0;border-radius:4px;
  font-weight:500;letter-spacing:.06em;text-transform:uppercase;cursor:pointer;
  height:2.1em;font-size:13px;padding:0 .9em;
  background:var(--ion-color-danger,#f04141);color:#fff;
  box-shadow:0 3px 1px -2px rgb(0 0 0 / 20%),0 2px 2px 0 rgb(0 0 0 / 14%),0 1px 5px 0 rgb(0 0 0 / 12%);
}
.ion-button.button-disabled{cursor:default;opacity:.5;pointer-events:none;box-shadow:none}
.ion-button .button-inner{display:flex;flex-flow:row nowrap;flex-shrink:0;align-items:center;justify-content:center;width:100%;height:100%}

/* ion-toast（md）的形态还原，具体数值未从旧版产物逐条核对 */
.ion-toast{position:absolute;left:0;right:0;bottom:0;display:flex;justify-content:center;pointer-events:none;z-index:100000}
.toast-wrapper{
  margin:0 16px 16px;max-width:700px;
  border-radius:4px;padding:14px 16px;
  background:#333;color:#fff;font-size:14px;line-height:20px;
  box-shadow:0 3px 5px -1px rgb(0 0 0 / 20%),0 6px 10px 0 rgb(0 0 0 / 14%),0 1px 18px 0 rgb(0 0 0 / 12%);
}
</style>
